import { decryptJwe, Encryption, generateJwt, JWTPayload, parseJwt } from "@credify/crypto";
import { hosts } from "./config";
import { decamelize, trimObject } from "./utils";
import type { OIDCOptions } from "../types";
import { CredifyClass } from "./common";
import Bluebird from "bluebird";
import { CONCURRENCY_LIMIT } from "./constants/common";

export class OIDC extends CredifyClass {
  /**
   * Generates a new OIDC URL.
   * For data consumer
   * @param id - Organization ID hosting this server
   * @param redirectUrl
   * @param scopes
   * @param options
   * @returns { oidcUrl: string, privateKey: string } - this private key is base64 URL encoded data.
   */
  public async initiateOIDC(
    id: string,
    redirectUrl: string,
    scopes: string[],
    options?: OIDCOptions
  ): Promise<{ oidcUrl: string; privateKey: string }> {
    if (!scopes.includes("openid")) {
      throw new Error("openid is required.");
    }
    const useEphemeralKey =
      options?.useEphemeralKey !== undefined ? options.useEphemeralKey : true;
    let privateKeyInBase64Url = "";
    let publicKeyInBase64Url = "";

    if (useEphemeralKey) {
      const enc = new Encryption();
      await enc.generateKeyPair();
      privateKeyInBase64Url = await enc.exportPrivateKeyInBase64Url();
      publicKeyInBase64Url = await enc.exportPublicKeyInBase64Url();
    }

    // NOTE: Add `-30` just in case of time collision.
    const timestamp = Number((new Date().getTime() / 1000).toFixed(0)) - 30;

    // Clone the array
    const _scopes = [...scopes];

    if (options?.offerCode) {
      if (options?.packageCode) {
        _scopes.push(`offer:${options.offerCode}:${options.packageCode}`);
      } else {
        _scopes.push(`offer:${options.offerCode}`);
      }
    }
    if (options?.dopCode) {
      if (options?.packageCode) {
        _scopes.push(`dop:${options.dopCode}:${options.packageCode}`);
      } else {
        _scopes.push(`dop:${options.dopCode}`);
      }
    }
    if (options?.orderId) {
      _scopes.push(`bnpl_order:${options.orderId}`)
    }

    const formattedScopes = _scopes.join(" ");
    const payload = {
      iss: id,
      encryptionPublicKey:
        publicKeyInBase64Url === "" ? undefined : publicKeyInBase64Url,
      iat: timestamp,
      scopes: formattedScopes,
    };

    const params = {
      clientId: id,
      redirectUri: redirectUrl,
      scope: formattedScopes,
      responseType: options?.responseType || "code",
      responseMode: options?.responseMode || "query",
      state: options?.state || Math.random().toString(),
      nonce: Math.random().toString(),
    };

    if (options?.userId) {
      // @ts-ignore
      params["entityId"] = options.userId;
    }
    if (options?.phoneNumber) {
      // @ts-ignore
      params["phoneNumber"] = options.phoneNumber.replace("+", "%2B");
    }

    const requestToken = generateJwt(this.signing, decamelize(payload));

    // @ts-ignore
    params["requestToken"] = requestToken;

    const { oidcCore } = hosts(this.config);

    const path = `oauth2/auth?${new URLSearchParams(
      decamelize(params)
    ).toString()}`;
    // NOTE: URLSearchParams is using `+` instead of `%20`, so replace them.
    const encodedPath = path.split("+").join("%20");

    return {
      oidcUrl: `${oidcCore}${encodedPath}`,
      privateKey: privateKeyInBase64Url,
    };
  }

  /**
   * Generates a new access token out of an authorization code.
   * @param id - client ID
   * @param code - Authorization code
   * @param redirectUri
   * @returns Promise<{ access_token: string, expires_in: number, id_token: string, scope: string, token_type: string }>
   */
  public async generateAccessToken(
    id: string,
    code: string,
    redirectUri: string
  ): Promise<{
    access_token: string;
    expires_in: number;
    id_token: string;
    scope: string;
    token_type: string;
  }> {
    const type = "authorization_code";

    const token = generateJwt(this.signing);

    const { oidcCore } = hosts(this.config);
    const path = "oauth2/token";
    const body = {
      code,
      grantType: type,
      redirectUri,
      clientSecret: token,
      clientId: id,
      clientType: "entity",
    };

    const res = await this.apiClient.formPost(`${oidcCore}${path}`, {
      ...decamelize(body),
    });
    // @ts-ignore
    return res.data;
  }

  private static async decryptClaimsV1(encryptedClaims: any, enc: Encryption) {
    const sid = encryptedClaims.sid;
    const sub = encryptedClaims.sub;
    const signing_public_key = encryptedClaims.signing_public_key;
    const transaction_id = encryptedClaims.transaction_id;
    const ext = encryptedClaims.ext;
    const aud = encryptedClaims.aud;
    const auth = encryptedClaims.auth;
    const auth_time = encryptedClaims.auth_time;
    const iat = encryptedClaims.iat;
    const iss = encryptedClaims.iss;
    const rat = encryptedClaims.rat;

    delete encryptedClaims["sid"];
    delete encryptedClaims["sub"];
    delete encryptedClaims["signing_public_key"];
    delete encryptedClaims["transaction_id"];
    delete encryptedClaims["ext"];
    delete encryptedClaims["aud"];
    delete encryptedClaims["auth"];
    delete encryptedClaims["auth_time"];
    delete encryptedClaims["iat"]
    delete encryptedClaims["iss"]
    delete encryptedClaims["rat"]

    try {
      const decrypted = await enc.decryptFieldByField(encryptedClaims);
      const trimmed = trimObject(decrypted);
      return {
        ...trimmed,
        sid,
        sub,
        signing_public_key,
        transaction_id,
        ext,
        aud,
        auth,
        auth_time,
        iat,
        iss,
        rat
      };
    } catch (e) {
      throw new Error("Decryption failed. Please check your private key.");
    }
  }

  /**
   * Handle decrypt claims version 2
   * @param encryptedClaims claims that encrypted
   * @example encryptedClaims
   *  {
        _claim_names: {address: 'address', address_commitment: 'address', email: 'email'},
        _claim_sources: {
          address: {JWT: 'eyJhbGciOiJSU0EtT0FFUC0yNTYiLCJlbmMiOiJBMjU2R0NNIn0...'},
          email: {JWT: 'eyJhbGciOiJSU0EtT0FFUC0yNTYiLCJlbmMiOiJBMjU2R0NNIn0...'}
        }
      }
   * @param enc Encryption object to manage keys and crypto operations
   * @returns 
   * @example Output
   * {
        _claim_names: {address: 'address', address_commitment: 'address', email: 'email'},
        _claim_sources: {
          address: {JWT: 'eyJhbGciOiJSU0EtT0FFUC0yNTYiLCJlbmMiOiJBMjU2R0NNIn0...'},
          email: {JWT: 'eyJhbGciOiJSU0EtT0FFUC0yNTYiLCJlbmMiOiJBMjU2R0NNIn0...'}
        },
        address: { locality: 'Châu Phú', region: 'An Giang', sublocality: 'Bình Mỹ' },
        address_commitment: 'gpx2VmQgEr4tJqpKYQh7MsPC2s0WDkz4Kx-pnMaaWd0',
        email: 'Scot42@gmail.com',
      }
   */
  private static async decryptClaimsV2(encryptedClaims: any, enc: Encryption) {
    // Decrypt all JWE in claim sources - encryptedClaims._claim_sources
    const privateKeyPem = await enc.exportPrivateKey();
    const jwes = encryptedClaims._claim_sources,
      claimNames = encryptedClaims._claim_names;
    const scopeNames = Object.keys(jwes);
    const promiseDecryptedArray = scopeNames.map((scopeName) => {
      const token = jwes[scopeName].JWT;
      const decryptedPromise = decryptJwe(privateKeyPem, token);
      return decryptedPromise;
    });

    // From decrypted JWE, parse all JWT payload
    const decryptedArray = await Bluebird.Promise.map(
      promiseDecryptedArray,
      (promiseDecrypted) => {
        return Promise.resolve(promiseDecrypted);
      },
      { concurrency: CONCURRENCY_LIMIT }
    );

    const decryptedClaimsGroupedByScope = decryptedArray.map((x, index) => {
      const scopeName = scopeNames[index];
      if (typeof x.payload === "string") {
        return {
          scopeName,
          value: parseJwt(x.payload).payload,
        };
      }
      return {
        scopeName,
        value: x.payload,
      };
    });

    // Filter decrypted claim values that exist in claimNames from encryptedClaims
    let jwtPayloads: { [key: string]: any } = {};
    for (const key in claimNames) {
      const scopeName = claimNames[key];
      const foundClaim = decryptedClaimsGroupedByScope.find(
        (x) => x.scopeName === scopeName
      );
      if (!foundClaim) continue;
      const claims = Object.keys(foundClaim.value);
      if (!claims.includes(key)) continue;
      jwtPayloads[key] = foundClaim.value[key];
    }

    const result = {
      ...encryptedClaims,
      ...jwtPayloads,
    };
    return result;
  }

  /**
   * Gets userinfo from OIDC
   * For data consumer
   * @param accessToken
   * @param privateKey - Base64 URL encoded private key or raw PEM private key starting with `-----BEGIN PRIVATE KEY-----`.
   */
  public async userinfo(
    accessToken: string,
    privateKey: string
  ): Promise<object> {
    // Call OIDC userinfo API and decrypt the response
    const authorization = `Bearer ${accessToken}`;
    const { oidcCore } = hosts(this.config);
    const path = "userinfo";
    const res = await this.apiClient.get(`${oidcCore}${path}`, {
      headers: { authorization },
    });

    const enc = new Encryption();
    if (privateKey.startsWith("-----BEGIN PRIVATE KEY-----")) {
      await enc.importPrivateKey(privateKey);
    } else {
      await enc.importPrivateKeyInBase64Url(privateKey);
    }
    const encryptedClaims: any = res.data;

    if (encryptedClaims.hasOwnProperty("_claim_names")) {
      return OIDC.decryptClaimsV2(encryptedClaims, enc)
    } else {
      return OIDC.decryptClaimsV1(encryptedClaims, enc)
    }
  }
}
